// src/pages/GpsDashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Si ya usas estilos globales del dashboard, mantenlos
import "./SensorDashboard.css";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores`;
const TZ = "America/Guayaquil";

const LAT_CAND = ["LAT", "LATITUDE"];
const LON_CAND = ["LON", "LONGITUDE"];

function pickVar(candidates, available) {
  const up = (available || []).map((v) => String(v).toUpperCase());
  return candidates.find((c) => up.includes(c)) || null;
}

function toMsSafe(ts) {
  if (ts == null) return NaN;
  if (typeof ts === "number") return ts < 1e11 ? ts * 1000 : ts;
  const tStr = String(ts);
  const t = Date.parse(tStr.includes("T") ? tStr : tStr.replace(" ", "T"));
  return Number.isNaN(t) ? NaN : t;
}

const fmtDT = (v) => {
  const ms = toMsSafe(v);
  return Number.isNaN(ms)
    ? "—"
    : new Date(ms).toLocaleString("es-EC", { timeZone: TZ });
};

function readValue(row) {
  const v = row?.value ?? row?.valor ?? row?.val ?? null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function readTs(row) {
  return row?.fecha_ms ?? row?.ts ?? row?.time ?? row?.fecha ?? null;
}

export default function GpsDashboard() {
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");

  const [availableVars, setAvailableVars] = useState([]);
  const [latVar, setLatVar] = useState(null);
  const [lonVar, setLonVar] = useState(null);

  const [latest, setLatest] = useState({ lat: null, lon: null, ts: null });

  // Trail / recorrido reciente
  const [trailPoints, setTrailPoints] = useState([]); // [{lat, lon, ts}]
  const [loadingTrail, setLoadingTrail] = useState(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const trailLayerRef = useRef(null);

  // 1) Cargar dispositivos GPS
  useEffect(() => {
    (async () => {
      try {
        const url = `${API_BASE}/devices?type=${encodeURIComponent("GPS")}`;
        const { data } = await axios.get(url, authHeader);
        const arr = Array.isArray(data) ? data : [];
        setDevices(arr.filter((d) => String(d.sensor_type) === "GPS"));
      } catch {
        setDevices([]);
      }
    })();
  }, [authHeader]);

  // 2) Selección por defecto
  useEffect(() => {
    if (!devices.length) {
      setSelectedDevice("");
      return;
    }
    if (
      !selectedDevice ||
      !devices.some((d) => String(d.id) === String(selectedDevice))
    ) {
      setSelectedDevice(String(devices[0].id));
    }
  }, [devices]); // eslint-disable-line

  // 3) Cargar variables del dispositivo
  useEffect(() => {
    if (!selectedDevice) {
      setAvailableVars([]);
      setLatVar(null);
      setLonVar(null);
      return;
    }

    (async () => {
      try {
        const url = `${API_BASE}/variables/${encodeURIComponent(
          selectedDevice
        )}`;
        const { data } = await axios.get(url, authHeader);
        const vars = Array.isArray(data) ? data : [];
        setAvailableVars(vars);

        setLatVar(pickVar(LAT_CAND, vars));
        setLonVar(pickVar(LON_CAND, vars));
      } catch {
        setAvailableVars([]);
        setLatVar(null);
        setLonVar(null);
      }
    })();
  }, [selectedDevice, authHeader]);

  // 4) Cargar últimos valores LAT/LON
  useEffect(() => {
    if (!selectedDevice) {
      setLatest({ lat: null, lon: null, ts: null });
      return;
    }

    const varsToFetch = [
      { key: "lat", v: latVar },
      { key: "lon", v: lonVar },
    ].filter((x) => x.v);

    if (!varsToFetch.length) {
      setLatest({ lat: null, lon: null, ts: null });
      return;
    }

    (async () => {
      try {
        const calls = varsToFetch.map(({ v }) => {
          const url = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
            selectedDevice
          )}&variable=${encodeURIComponent(v)}&limit=1`;
          return axios.get(url, authHeader);
        });

        const results = await Promise.allSettled(calls);

        const next = { lat: null, lon: null, ts: null };

        results.forEach((r, idx) => {
          const meta = varsToFetch[idx];
          if (r.status !== "fulfilled") return;

          const arr = Array.isArray(r.value.data) ? r.value.data : [];
          const row = arr[0];
          if (!row) return;

          const value = readValue(row);
          const tsRaw = readTs(row);
          const tsMs = toMsSafe(tsRaw);

          next[meta.key] = value;
          if (Number.isFinite(tsMs)) {
            next.ts = Math.max(next.ts ?? 0, tsMs);
          }
        });

        setLatest(next);
      } catch {
        setLatest({ lat: null, lon: null, ts: null });
      }
    })();
  }, [selectedDevice, latVar, lonVar, authHeader]);

  // 5) Cargar recorrido reciente (solo frontend)
  useEffect(() => {
    if (!selectedDevice || !latVar || !lonVar) {
      setTrailPoints([]);
      return;
    }

    (async () => {
      setLoadingTrail(true);
      try {
        const LIMIT = 25;

        const urlLat = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
          selectedDevice
        )}&variable=${encodeURIComponent(latVar)}&limit=${LIMIT}`;

        const urlLon = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
          selectedDevice
        )}&variable=${encodeURIComponent(lonVar)}&limit=${LIMIT}`;

        const [resLat, resLon] = await Promise.all([
          axios.get(urlLat, authHeader),
          axios.get(urlLon, authHeader),
        ]);

        const arrLat = Array.isArray(resLat.data) ? resLat.data : [];
        const arrLon = Array.isArray(resLon.data) ? resLon.data : [];

        const minLen = Math.min(arrLat.length, arrLon.length);

        const combined = [];
        for (let i = 0; i < minLen; i++) {
          const latRow = arrLat[i];
          const lonRow = arrLon[i];

          const lat = readValue(latRow);
          const lon = readValue(lonRow);

          const tsRaw = readTs(latRow) ?? readTs(lonRow);
          const tsMs = toMsSafe(tsRaw);

          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            combined.push({
              lat,
              lon,
              ts: Number.isFinite(tsMs) ? tsMs : null,
            });
          }
        }

        // Los endpoints suelen venir DESC, así que invertimos para dibujar ruta lógica
        setTrailPoints(combined.reverse());
      } catch {
        setTrailPoints([]);
      } finally {
        setLoadingTrail(false);
      }
    })();
  }, [selectedDevice, latVar, lonVar, authHeader]);

  // 6) Inicializar mapa (una sola vez)
  useEffect(() => {
    const target = document.getElementById("gpsMap");
    if (!target) return;

    if (!mapRef.current) {
      mapRef.current = L.map(target, { scrollWheelZoom: true }).setView(
        [0, 0],
        2
      );
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
      }).addTo(mapRef.current);
    }
  }, []);

  // 7) Actualizar marcador + centrar
  useEffect(() => {
    if (!mapRef.current) return;

    const lat = latest.lat;
    const lon = latest.lon;

    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      mapRef.current.setView([lat, lon], 16);

      if (!markerRef.current) {
        markerRef.current = L.marker([lat, lon]).addTo(mapRef.current);
      } else {
        markerRef.current.setLatLng([lat, lon]);
      }
    }
  }, [latest.lat, latest.lon]);

  // 8) Dibujar/actualizar ruta (trail)
  useEffect(() => {
    if (!mapRef.current) return;

    if (trailLayerRef.current) {
      trailLayerRef.current.remove();
      trailLayerRef.current = null;
    }

    if (trailPoints.length >= 2) {
      const latlngs = trailPoints.map((p) => [p.lat, p.lon]);
      trailLayerRef.current = L.polyline(latlngs, {
        weight: 4,
        opacity: 0.8,
      }).addTo(mapRef.current);

      // Ajuste de encuadre suave cuando hay ruta
      try {
        mapRef.current.fitBounds(trailLayerRef.current.getBounds(), {
          padding: [30, 30],
        });
      } catch {}
    }
  }, [trailPoints]);

  // 9) Forzar ajuste de tamaño del mapa
  useEffect(() => {
    const t = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 120);
    return () => clearTimeout(t);
  }, [selectedDevice]);

  const goHome = () => navigate("/seleccionar-tipo", { replace: false });

  const selectedObj = devices.find(
    (d) => String(d.id) === String(selectedDevice)
  );

  const imgSrc = selectedObj?.image_url || selectedObj?.photo_url || null;

  // ===== Estilos layout “GPS-first” =====
  const pageStyle = {
    paddingBottom: 30,
  };

  const headerStyle = {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
    marginBottom: 10,
  };

  const titleStyle = {
    margin: 0,
    fontSize: "clamp(28px, 2.6vw, 36px)",
    fontWeight: 900,
    letterSpacing: "1px",
    color: "#0b67b2",
    textTransform: "uppercase",
  };

  const layoutStyle = {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "minmax(300px, 360px) minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  };

  const leftColStyle = {
    display: "grid",
    gap: 16,
  };

  const kpiRowStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  };

  const kpiStyle = {
    padding: "18px 18px 14px 18px",
  };

  const kpiTitle = {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "1px",
    opacity: 0.75,
  };

  const kpiValue = {
    fontSize: "clamp(28px, 2.2vw, 38px)",
    fontWeight: 900,
    marginTop: 6,
  };

  const kpiTime = {
    fontSize: 11,
    opacity: 0.7,
    marginTop: 4,
  };

  const trackerCardStyle = {
    display: "flex",
    gap: 14,
    alignItems: "center",
    padding: 18,
  };

  const imgBoxStyle = {
    width: 96,
    height: 96,
    borderRadius: 14,
    background: "rgba(255,255,255,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.06)",
    flexShrink: 0,
  };

  const mapCardStyle = {
    padding: 12,
  };

  const mapInnerStyle = {
    width: "100%",
    height: "min(72vh, 640px)",
    borderRadius: 18,
    overflow: "hidden",
  };

  const miniListStyle = {
    marginTop: 10,
    display: "grid",
    gap: 6,
    fontSize: 12,
    opacity: 0.85,
  };

  return (
    <div className="sensor-dashboard" style={pageStyle}>
      {/* Botón home flotante */}
      <div
        className="action-float left"
        style={{
          position: "fixed",
          top: 18,
          left: 18,
          zIndex: 9999,
        }}
      >
        <button
          type="button"
          className="btn-solid btn-home btn-circle"
          title="Inicio"
          onClick={goHome}
          style={{ overflow: "visible" }}
        >
          <svg
            className="icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 3.172l9 8V20a2 2 0 0 1-2 2h-5v-6H10v6H5a2 2 0 0 1-2-2v-8.828l9-8zM3 10.172L12 2l9 8.172V11h-2V9.414l-7-6.293-7 6.293V11H3v-.828z" />
          </svg>
        </button>
      </div>

      {/* Título */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>PANEL GPS</h1>
      </div>

      {/* Barra superior */}
      <div className="toolbar-row">
        <select
          value={selectedDevice}
          onChange={(e) => setSelectedDevice(e.target.value)}
        >
          <option value="">Seleccionar tracker</option>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name || d.id}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
      </div>

      {/* KPIs compactos */}
      <div style={{ marginTop: 16, ...kpiRowStyle }}>
        <div className="card glass" style={kpiStyle}>
          <div style={kpiTitle}>LATITUD</div>
          <div style={kpiValue}>
            {latest.lat != null ? latest.lat.toFixed(6) : "—"}°
          </div>
          <div style={kpiTime}>
            {latest.ts ? fmtDT(latest.ts) : "sin datos"}
          </div>
        </div>

        <div className="card glass" style={kpiStyle}>
          <div style={kpiTitle}>LONGITUD</div>
          <div style={kpiValue}>
            {latest.lon != null ? latest.lon.toFixed(6) : "—"}°
          </div>
          <div style={kpiTime}>
            {latest.ts ? fmtDT(latest.ts) : "sin datos"}
          </div>
        </div>
      </div>

      {/* Layout principal: izquierda info, derecha mapa gigante */}
      <div style={layoutStyle}>
        {/* Columna izquierda */}
        <div style={leftColStyle}>
          {/* Tarjeta tracker */}
          <div className="card glass" style={trackerCardStyle}>
            <div style={imgBoxStyle}>
              {imgSrc ? (
                <img
                  src={imgSrc}
                  alt={selectedObj?.name || "GPS"}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div
                  style={{
                    fontWeight: 900,
                    color: "#0b67b2",
                    textAlign: "center",
                    padding: 6,
                  }}
                >
                  GPS
                </div>
              )}
            </div>

            <div style={{ width: "100%" }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>
                {selectedObj?.name || "—"}
              </div>

              <div style={{ marginTop: 8, fontSize: 12 }}>
                <div>
                  <strong>ID:</strong> {selectedDevice || "—"}
                </div>
                <div style={{ marginTop: 4 }}>
                  <strong>Variables:</strong>{" "}
                  {availableVars.length ? availableVars.join(", ") : "—"}
                </div>
                <div style={{ marginTop: 4 }}>
                  <strong>Última actualización:</strong>{" "}
                  {latest.ts ? fmtDT(latest.ts) : "—"}
                </div>
              </div>

              {/* Mini estado visual */}
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: "rgba(11,103,178,0.12)",
                    color: "#0b67b2",
                  }}
                >
                  GPS
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: trailPoints.length ? "rgba(0,128,0,0.12)" : "rgba(0,0,0,0.08)",
                    color: trailPoints.length ? "#0a7a2f" : "#555",
                  }}
                >
                  {trailPoints.length ? "Recorrido disponible" : "Sin recorrido"}
                </span>
              </div>
            </div>
          </div>

          {/* Últimas posiciones (da vida al panel) */}
          <div className="card glass" style={{ padding: 16 }}>
            <div style={{ fontWeight: 900, fontSize: 14 }}>
              Últimas posiciones
            </div>

            {loadingTrail ? (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                Cargando recorrido...
              </div>
            ) : !trailPoints.length ? (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                Aún no hay suficientes puntos para mostrar historial.
              </div>
            ) : (
              <div style={miniListStyle}>
                {trailPoints
                  .slice(-5)
                  .reverse()
                  .map((p, i) => (
                    <div key={i}>
                      <strong>
                        {Number.isFinite(p.lat) ? p.lat.toFixed(6) : "—"},{" "}
                        {Number.isFinite(p.lon) ? p.lon.toFixed(6) : "—"}
                      </strong>{" "}
                      <span style={{ opacity: 0.7 }}>
                        {p.ts ? `· ${fmtDT(p.ts)}` : ""}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Mapa gigante */}
        <div className="card glass" style={mapCardStyle}>
          <div id="gpsMap" style={mapInnerStyle} />
          {/* Pie sutil */}
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              opacity: 0.65,
              padding: "0 4px 2px 4px",
            }}
          >
            <span>
              {trailPoints.length
                ? `Recorrido: ${trailPoints.length} puntos`
                : "Recorrido no disponible"}
            </span>
            <span>
              {latest.ts ? `Último dato: ${fmtDT(latest.ts)}` : ""}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
